import React, { ForwardedRef, forwardRef, useState, useEffect } from 'react';
import { ServerStatusResponse } from '@/server/responses';
import Spinner from '../objects/Spinner';
import { SplashState } from '@/state/SplashState';
import { useDispatch } from '@/state/store';
import SvgCloseGray from '@/components/objects/CloseGray';
import { fetchLesStatus } from '@/actions/metaActions';

interface SplashProps {
    handleWindowChange: (window: SplashState) => void;
}
export const Splash = forwardRef(
    ({ handleWindowChange }: SplashProps, ref: ForwardedRef<HTMLDialogElement>) => {
        Splash.displayName = 'Splash';

        const [serverInfo, setServerInfo] = useState<ServerStatusResponse | null>(null);
        const dispatch = useDispatch();
        useEffect(() => {
            dispatch(fetchLesStatus())
                .unwrap()
                .then(i => setServerInfo(i))
                .catch(e => console.warn(e));
        }, [dispatch]);
        return (
            <>
                <dialog
                    ref={ref}
                    className="c-splash c-splash-container"
                    aria-expanded="true"
                    aria-label={'Nyttig informasjon om Vegkart'}
                    aria-modal="true"
                >
                    <header className="c-splash-header">
                        <div className="c-splash-header__text" id={'splash-header'}>
                            Nyttig informasjon om Vegkart
                        </div>
                        <button
                            type="button"
                            title="Lukk"
                            id="close_splash_header"
                            onClick={() => handleWindowChange(SplashState.NONE)}
                            className="info-message__close-button"
                            aria-label="Lukk dialog"
                        >
                            <SvgCloseGray
                                aria-hidden
                                height={16}
                                width={16}
                                className="svv-modal_close_icon"
                            />
                            <span>Lukk</span>
                        </button>
                    </header>
                    <section className="c-splash-main">
                        <div className="c-splash-main__grid-container">
                            <div className="c-splash-main__grid-container-item">
                                <p>Informasjon om Vegkart og innholdet i Nasjonal vegdatabank.</p>
                                <a
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    href="https://www.vegvesen.no/fag/teknologi/nasjonal+vegdatabank/kart"
                                >
                                    Les mer om Vegkart.
                                </a>
                            </div>
                            <div className="c-splash-main__grid-container-item">
                                <p>Brukerveiledning for Vegkart, og opptak av Webinar.</p>
                                <a
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    href="https://nvdb.atlas.vegvesen.no/docs/category/brukerveiledning"
                                >
                                    Finn informasjon på nvdb.no.
                                </a>
                            </div>
                        </div>
                        <div className="c-splash-main__highlight">
                            <p>
                                Vegsystemreferansen er en rapporterings- og oppslagsnøkkel for enkelt å vite
                                hvor man er på vegen. For europa-, riks- og fylkesveger, er denne unik på
                                landsbasis. For kommunale, private og skogsveger, er denne unik innenfor den
                                enkelte kommune.
                            </p>
                            <div
                                className="o-img-systemreferanse c-splash-divider__roadsystemreference"
                                role="image"
                            />
                            <a
                                target="_blank"
                                rel="noreferrer noopener"
                                href="https://www.vegvesen.no/fag/teknologi/nasjonal-vegdatabank/vegreferansesystem/"
                            >
                                Mer informasjon om referansesystemet i Nasjonal vegdatabank.
                            </a>
                        </div>
                        <div className="c-splash-main__paragraph">
                            <p>
                                Datagrunnlaget fra NVDB ble sist oppdatert:&nbsp;
                                <span className="c-splash-main__bold">
                                    {serverInfo ? (
                                        serverInfo.datagrunnlag_sist_oppdatert
                                    ) : (
                                        <Spinner className="c-splash-main__spinner-icon" />
                                    )}
                                </span>
                            </p>
                            <p>Alle data er fritt tilgjengelig etter Norsk lisens for offentlige data.</p>
                            <p>
                                Stedsnavn hentes fra{' '}
                                <a href="https://www.kartverket.no/" target="_blank">
                                    Sentralt stedsnavnregister (SSR) ©Kartverket
                                </a>
                                .
                            </p>
                        </div>
                    </section>
                    <footer className="c-splash-footer">
                        <hr />
                        <div className="c-splash-footer-interactive">
                            <div className="c-splash-footer-link-container">
                                <a
                                    className="c-splash-footer-interactive__item"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    href="https://www.vegvesen.no/fag/teknologi/nasjonal-vegdatabank/kontakt-nvdb/"
                                >
                                    Kontakt oss
                                </a>
                            </div>

                            <div className="c-splash-footer-link-container">
                                <a
                                    className="c-splash-footer-interactive__item"
                                    type="button"
                                    title="Versjonslogg"
                                    aria-label="Versjonslogg"
                                    onClick={() => handleWindowChange(SplashState.CHANGELOG)}
                                >
                                    Versjonslogg
                                </a>
                            </div>

                            <div className="c-splash-footer-link-container">
                                <a
                                    className="c-splash-footer-interactive__item"
                                    type="button"
                                    title="Personvernerklæring"
                                    aria-label="Personvernerklæring"
                                    onClick={() => handleWindowChange(SplashState.POLICY)}
                                >
                                    Personvernerklæring
                                </a>
                            </div>

                            <div className="c-splash-footer-link-container">
                                <a
                                    className="c-splash-footer-interactive__item"
                                    target="_blank"
                                    rel="noreferrer noopener"
                                    href="https://uustatus.no/nb/erklaringer/publisert/abcf2dfc-797f-4774-a5e4-511a3b5a4aed"
                                >
                                    Tilgjengelighetserklæring
                                </a>
                            </div>
                        </div>
                    </footer>
                </dialog>
            </>
        );
    }
);
